/*
 * Lunvelis Partners · formularios con respuesta real.
 * Mejora los formularios de contacto de Shopify (contacto, diagnóstico, reuniones, colaboración):
 *   - envío sin recargar y confirmación SOLO cuando Shopify (o el servicio de solicitudes) acepta el envío;
 *   - los datos se conservan si algo falla; el botón vuelve a su estado (también al volver con «Atrás»);
 *   - bloqueo de envíos dobles y aviso si se repite una solicitud idéntica reciente;
 *   - borrador automático en este dispositivo, con opción de descartarlo;
 *   - validación con avisos claros (sin alertas nativas);
 *   - reuniones: fecha, hora y zona horaria coherentes entre sí.
 * Si algo no se puede mejorar (p. ej. verificación anti-spam de Shopify), se usa el envío normal del formulario.
 */
(() => {
  if (window.__lpForms) return;
  window.__lpForms = true;
  if (window.Shopify && window.Shopify.designMode) return;

  const ui = () => window.LunvelisUI;
  const toast = (options) => (ui() ? ui().toast(options) : null);
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const ASSESSMENT_KEY = 'lunvelis-partners-assessment-v2';
  const SKIP = new Set(['authenticity_token', 'form_type', 'utf8', 'contact[Contexto]']);
  const KINDS = [
    { match: /^PartnersContact/, kind: 'contact', title: 'Mensaje de contacto', successTitle: 'Mensaje enviado', text: 'El equipo de Lunvelis recibió tu mensaje y responderá al correo que indicaste. No hay respuesta automática ni garantía de colaboración.', fields: ['contact[Interés]', 'contact[name]', 'contact[email]'] },
    { match: /^PartnersAssessment/, kind: 'assessment', title: 'Diagnóstico empresarial', successTitle: 'Diagnóstico enviado', text: 'Recibimos tus respuestas. Tu resumen ya está disponible en este dispositivo y el equipo podrá usarlo para orientarte.', fields: ['contact[name]', 'contact[Empresa]', 'contact[email]'], actions: [{ label: 'Ver mi resumen', href: '/pages/partners-snapshot' }] },
    { match: /^PartnersMeetRequest/, kind: 'meeting', title: 'Solicitud de reunión', successTitle: 'Solicitud de reunión enviada', text: 'Esto es una solicitud, no una reserva confirmada. El equipo revisará la disponibilidad y te escribirá al correo indicado para confirmar fecha, hora y enlace.', pending: true, fields: ['contact[Tipo de sesión]', 'contact[Modalidad]', 'contact[Fecha preferida]', 'contact[Horario preferido]', 'contact[Zona horaria]', 'contact[email]'] },
    { match: /^PartnersCollaboration/, kind: 'collaboration', title: 'Propuesta de colaboración', successTitle: 'Propuesta enviada', text: 'El equipo revisará tu propuesta y te responderá al correo indicado. Enviarla no garantiza una colaboración.', fields: ['contact[Tipo]', 'contact[name]', 'contact[email]'] }
  ];
  const metaFor = (form) => KINDS.find((item) => item.match.test(form.id || '')) || { kind: 'contact', title: 'Solicitud', successTitle: 'Solicitud enviada', text: 'El equipo de Lunvelis recibió tu solicitud y responderá al correo que indicaste.', fields: ['contact[name]', 'contact[email]'] };

  const storage = () => (ui() ? ui().store : { get: () => null, set: () => false, remove: () => {} });
  const hash = (text) => { let h = 2166136261; for (let i = 0; i < text.length; i += 1) { h ^= text.charCodeAt(i); h = Math.imul(h, 16777619); } return (h >>> 0).toString(36); };
  const signature = (form, data) => {
    const parts = [];
    for (const [key, value] of data.entries()) { if (typeof value !== 'string' || SKIP.has(key) || key === 'lp_company_site') continue; parts.push(`${key}=${value.trim().toLowerCase()}`); }
    return `${form.id}:${hash(parts.sort().join('|'))}`;
  };
  const SENT_KEY = 'lp.sent.v1';
  const recent = (sig) => (storage().get(SENT_KEY, []) || []).find((item) => item.sig === sig && Date.now() - item.ts < 15 * 60 * 1000);
  const remember = (sig) => storage().set(SENT_KEY, [{ sig, ts: Date.now() }, ...(storage().get(SENT_KEY, []) || []).filter((item) => Date.now() - item.ts < 864e5)].slice(0, 20));

  /* ---------- registro de solicitudes (agenda incluida) ---------- */
  const offsetMs = (zone, ts) => {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: zone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }).formatToParts(new Date(ts));
    const m = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return Date.UTC(+m.year, +m.month - 1, +m.day, +m.hour, +m.minute, +m.second) - ts;
  };
  const zonedToUtc = (date, time, zone) => {
    const [y, mo, d] = date.split('-').map(Number);
    const [h, mi] = time.split(':').map(Number);
    const guess = Date.UTC(y, mo - 1, d, h, mi);
    let ts = guess - offsetMs(zone, guess);
    ts = guess - offsetMs(zone, ts);
    return ts;
  };
  const buildEntry = (meta, data) => {
    const entry = { title: meta.title, kind: meta.kind, status: meta.pending ? 'Solicitud enviada · pendiente de confirmar' : 'Enviada' };
    if (meta.kind === 'meeting') {
      const date = data.get('contact[Fecha preferida]') || '';
      const time = data.get('contact[Horario preferido]') || '';
      const zone = data.get('contact[Zona horaria]') || 'UTC';
      const type = data.get('contact[Tipo de sesión]') || '';
      const mode = data.get('contact[Modalidad]') || '';
      entry.title = type ? `Reunión · ${type}` : meta.title;
      entry.detail = [mode, date && time ? `${date} a las ${time} (${zone})` : ''].filter(Boolean).join(' · ');
      try {
        const start = zonedToUtc(date, time, zone);
        entry.ics = { start: new Date(start).toISOString(), end: new Date(start + 30 * 60000).toISOString(), summary: 'Solicitud de reunión con Lunvelis Partners (por confirmar)', description: `Tipo: ${type}. Modalidad: ${mode}. Esta es una solicitud, no una reserva confirmada: el equipo te confirmará fecha, hora y enlace por correo. Duración por confirmar.` };
      } catch (error) { /* huso horario no reconocido: sin archivo de calendario */ }
    } else if (meta.kind === 'contact') entry.detail = data.get('contact[Interés]') || '';
    else if (meta.kind === 'collaboration') entry.detail = data.get('contact[Tipo]') || '';
    return entry;
  };
  const PENDING_KEY = 'lp.pending.v1';

  /* ---------- borrador automático ---------- */
  const draftKey = (form) => `lp.draft.v1:${location.pathname}:${form.id}`;
  const collect = (form) => {
    const out = {};
    for (const el of form.elements) {
      if (!el.name || el.disabled || SKIP.has(el.name) || el.name === 'lp_company_site' || el.hasAttribute('data-assessment-body')) continue;
      if (['hidden', 'password', 'file', 'submit', 'button'].includes(el.type)) continue;
      if (el.type === 'checkbox' || el.type === 'radio') { if (el.checked) (out[el.name] = out[el.name] || []).push(el.value); } else if (el.value) out[el.name] = el.value;
    }
    return out;
  };
  const savedOnce = new Set();
  const saveDraft = (form) => {
    const data = collect(form);
    if (Object.keys(data).length) {
      storage().set(draftKey(form), { ts: Date.now(), data });
      if (!savedOnce.has(form.id)) { savedOnce.add(form.id); toast({ type: 'success', id: 'lp-draft-saved', title: 'Borrador guardado', message: 'Puedes salir y volver: tus datos quedan en este dispositivo.', duration: 3200 }); }
    } else storage().remove(draftKey(form));
  };
  const restoreDraft = (form) => {
    const saved = storage().get(draftKey(form));
    if (!saved || Date.now() - saved.ts > 3 * 864e5) { storage().remove(draftKey(form)); return; }
    let restored = 0;
    for (const [name, value] of Object.entries(saved.data)) {
      const fields = [...form.elements].filter((el) => el.name === name);
      if (!fields.length) continue;
      if (fields[0].type === 'radio' || fields[0].type === 'checkbox') {
        if (fields.some((el) => el.checked)) continue;
        for (const el of fields) if ((value || []).includes(el.value)) { el.checked = true; el.dispatchEvent(new Event('change', { bubbles: true })); restored += 1; }
      } else if (!fields[0].value) { fields[0].value = value; fields[0].dispatchEvent(new Event('input', { bubbles: true })); fields[0].dispatchEvent(new Event('change', { bubbles: true })); restored += 1; }
    }
    if (restored) toast({ type: 'info', id: `draft-${form.id}`, title: 'Recuperamos tu borrador', message: 'Los datos que habías escrito siguen aquí. Se guardan solo en este dispositivo.', action: { label: 'Descartar', onClick: () => { storage().remove(draftKey(form)); location.reload(); } } });
  };

  /* ---------- estados del botón ---------- */
  const submitButtons = (form) => [...form.querySelectorAll('button[type="submit"], input[type="submit"]')];
  const rememberLabels = (form) => submitButtons(form).forEach((b) => { if (!b.dataset.lpLabel) b.dataset.lpLabel = b.tagName === 'INPUT' ? b.value : b.innerHTML; });
  const setBusy = (form, busy) => {
    form.toggleAttribute('data-lp-busy', busy);
    submitButtons(form).forEach((b) => {
      b.disabled = busy;
      b.setAttribute('aria-busy', String(busy));
      if (busy) { if (b.tagName === 'BUTTON') b.textContent = 'Enviando…'; } else if (b.dataset.lpLabel) { if (b.tagName === 'INPUT') b.value = b.dataset.lpLabel; else b.innerHTML = b.dataset.lpLabel; }
    });
  };
  window.addEventListener('pageshow', (event) => { if (event.persisted) { document.querySelectorAll('form[data-lp-form]').forEach((form) => setBusy(form, false)); document.querySelectorAll('.lp-toast--progress .lp-toast__close').forEach((button) => button.click()); } });

  /* ---------- confirmación ---------- */
  const labelFor = (form, name) => { const el = form.elements[name]; const label = el && el.labels && el.labels[0]; const text = label ? [...label.childNodes].filter((n) => n.nodeType === 3).map((n) => n.textContent).join(' ') : ''; return (text || name.replace(/^contact\[|\]$/g, '')).replace(/\s+/g, ' ').trim(); };
  const rowsFor = (form, meta, data) => (meta.fields || []).map((name) => [labelFor(form, name), data.get(name)]).filter(([, value]) => value).map(([term, value]) => [term, String(value)]);
  const renderSent = ({ host, meta, rows, entry, emailed }) => {
    if (!host || document.querySelector('.lp-sent')) return;
    const panel = document.createElement('div');
    panel.className = 'lp-sent' + (meta.pending ? ' lp-sent--pending' : '');
    panel.setAttribute('role', 'status');
    panel.setAttribute('tabindex', '-1');
    panel.innerHTML = '<span class="lp-sent__mark" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="' + (meta.pending ? 'M12 7v5l3 2' : 'M5 12.5l4.2 4.2L19 7') + '"/></svg></span>';
    const h = document.createElement('h3'); h.textContent = meta.successTitle;
    const p = document.createElement('p'); p.textContent = meta.text + (emailed ? ' También te enviamos un correo de recepción.' : '');
    panel.append(h, p);
    if (rows && rows.length) {
      const dl = document.createElement('dl');
      for (const [term, value] of rows) { const row = document.createElement('div'); const dt = document.createElement('dt'); dt.textContent = term; const dd = document.createElement('dd'); dd.textContent = value; row.append(dt, dd); dl.append(row); }
      panel.append(dl);
    }
    const actions = document.createElement('div');
    actions.className = 'lp-sent__actions';
    for (const item of meta.actions || []) { const a = document.createElement('a'); a.className = 'lp-btn lp-btn--dark-glass lp-btn--sm'; a.href = item.href; a.textContent = item.label; actions.append(a); }
    if (meta.kind === 'meeting') {
      if (entry && entry.ics && ui()) { const add = document.createElement('button'); add.type = 'button'; add.className = 'lp-btn lp-btn--light lp-btn--sm'; add.textContent = 'Añadir al calendario'; add.addEventListener('click', () => ui().downloadIcs(entry.ics)); actions.append(add); }
      const log = document.createElement('a'); log.className = 'lp-link'; log.href = '#lp-agenda-log'; log.textContent = 'Ver mi registro de agenda'; actions.append(log);
    }
    const sound = document.createElement('button'); sound.type = 'button'; sound.className = 'lp-sel__remove'; sound.dataset.lpSoundToggle = ''; sound.textContent = ui() && ui().soundOn() ? 'Sonido de confirmación: activado' : 'Sonido de confirmación: silenciado'; sound.setAttribute('aria-pressed', String(!ui() || ui().soundOn()));
    const again = document.createElement('a'); again.className = 'lp-link'; again.href = location.pathname; again.textContent = 'Enviar otra solicitud';
    again.addEventListener('click', (event) => { event.preventDefault(); location.href = location.pathname + '?intro=0'; });
    const home = document.createElement('a'); home.className = 'lp-link'; home.href = '/'; home.textContent = 'Volver al inicio';
    actions.append(again, home, sound);
    panel.append(actions);
    host.hidden = true;
    host.after(panel);
    panel.scrollIntoView({ block: 'center', behavior: reduced.matches ? 'auto' : 'smooth' });
    panel.focus({ preventScroll: true });
  };

  /* ---------- servicio de solicitudes opcional (correos de recepción) ---------- */
  const endpoint = () => document.querySelector('meta[name="lp-requests-endpoint"]')?.content || '';
  const viaService = async (form, meta, data) => {
    const base = endpoint();
    if (!base) return null;
    try {
      const boot = await fetch(base.replace(/public-request$/, 'public-bootstrap'), { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!boot.ok) return null;
      const { csrf } = await boot.json();
      const fields = {};
      for (const [key, value] of data.entries()) if (typeof value === 'string' && !SKIP.has(key) && key !== 'lp_company_site') fields[key.replace(/^contact\[|\]$/g, '')] = value;
      if (data.get('contact[body]')) fields.body = data.get('contact[body]');
      const res = await fetch(base, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', 'X-Partners-CSRF': csrf }, body: JSON.stringify({ kind: meta.kind, form: form.id.replace(/-.*$/, ''), fields, page: location.pathname }) });
      if (!res.ok) return null;
      const json = await res.json();
      /* Solo cuenta como entregada si el equipo fue notificado; si no, el envío normal de Shopify sigue siendo el respaldo. */
      return json && json.ok && json.notified ? json : null;
    } catch (error) { return null; }
  };

  /* ---------- envío ---------- */
  /* Shopify protege sus formularios con hCaptcha y envía por su cuenta: por eso no se intercepta el envío.
     Antes de que continúe se guarda un registro «pendiente» y, cuando Shopify devuelve a la persona a esta página
     con su confirmación (con o sin verificación), se muestra la alerta, el sonido y el registro. */
  const cameFromVerification = () => {
    try {
      const nav = performance.getEntriesByType('navigation')[0];
      if (nav && nav.type !== 'navigate') return false;
      const ref = new URL(document.referrer);
      return ref.origin === location.origin && /^\/(challenge|contact)\/?$/.test(ref.pathname);
    } catch (error) { return false; }
  };
  const postedOnPage = () => /[?&]contact_posted=true/.test(location.search) || !!document.querySelector('[data-assessment-sent], .partners-alert--success, form.partners-route__form > p[role="status"]') || cameFromVerification();
  const showChallengeSummary = () => {
    const host = document.querySelector('[data-lp-challenge-summary]');
    const pending = host && storage().get(PENDING_KEY);
    if (!pending) return;
    const strong = document.createElement('strong'); strong.textContent = 'Tu solicitud: ' + (pending.entry && pending.entry.title || 'solicitud');
    host.append(strong);
    if (pending.entry && pending.entry.detail) { const span = document.createElement('span'); span.textContent = pending.entry.detail; host.append(span); }
    const note = document.createElement('span'); note.textContent = 'Se enviará al completar la verificación. Después te mostramos la confirmación.'; host.append(note);
    host.hidden = false;
  };
  const metaByKind = (kind) => KINDS.find((item) => item.kind === kind) || metaFor({ id: '' });
  const finalizePending = () => {
    const pending = storage().get(PENDING_KEY);
    if (!pending) return;
    if (Date.now() - pending.ts > 30 * 60 * 1000) { storage().remove(PENDING_KEY); return; }
    if (pending.path !== location.pathname || !postedOnPage()) return;
    storage().remove(PENDING_KEY);
    remember(pending.sig);
    storage().remove('lp.draft.v1:' + pending.path + ':' + pending.formId);
    if (pending.kind === 'assessment') { try { const saved = JSON.parse(sessionStorage.getItem(ASSESSMENT_KEY) || 'null') || {}; sessionStorage.setItem(ASSESSMENT_KEY, JSON.stringify({ ...saved, submitted: true })); } catch (error) { /* sin sessionStorage */ } }
    const marker = document.querySelector('[data-assessment-sent], .partners-alert--success, form.partners-route__form > p[role="status"]');
    const host = marker ? (marker.closest('form') || marker) : document.querySelector('form[data-lp-form]#' + pending.formId) || document.querySelector('form[data-lp-form]');
    const meta = metaByKind(pending.kind);
    const go = () => {
      if (!ui()) { setTimeout(go, 120); return; }
      if (pending.rid && ui().requests.list().some((item) => item.ts === pending.rid)) ui().requests.update(pending.rid, { status: pending.entry.status }); else ui().requests.add(pending.entry);
      toast({ type: 'success', sound: 'success', title: pending.successTitle, message: pending.toastText, duration: 12000 });
      renderSent({ host, meta, rows: pending.rows, entry: pending.entry, emailed: false });
      if (window.LunvelisFx && !pending.pending) setTimeout(() => window.LunvelisFx.confetti(document.querySelector('.lp-sent') || document.body), 350);
    };
    go();
  };

  const savePending = (record) => {
    const rid = ui() ? ui().requests.add({ ...record.entry, status: 'Registrada · falta completar la verificación de Shopify' }) : 0;
    storage().set(PENDING_KEY, { ...record, rid, ts: Date.now() });
  };

  const onCapture = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('form[data-lp-form]') || form.dataset.lpNative) return;
    if (!form.checkValidity()) return;
    const meta = metaFor(form);
    const data = new FormData(form);
    for (const key of ['date', 'end_date']) data.delete(key);
    const entry = buildEntry(meta, data);
    data.delete('lp_company_site');
    const sig = signature(form, data);
    if (recent(sig) && !form.dataset.lpForce) {
      event.preventDefault(); event.stopImmediatePropagation();
      toast({ type: 'warning', sound: 'info', id: 'lp-dup', title: 'Ya enviaste esta solicitud', message: 'Detectamos una solicitud idéntica hace pocos minutos. Si no recibiste respuesta aún, el equipo ya la tiene.', action: { label: 'Enviar de todos modos', onClick: () => { form.dataset.lpForce = '1'; form.requestSubmit(); } } });
      return;
    }
    delete form.dataset.lpForce;
    const detail = entry.detail ? ' Quedó registrada: ' + entry.detail + '.' : '';
    const record = { formId: form.id, path: location.pathname, kind: meta.kind, sig, pending: !!meta.pending, entry, rows: rowsFor(form, meta, data), successTitle: meta.successTitle, toastText: (meta.pending ? 'Aún no es una reserva confirmada: el equipo te confirmará por correo.' : 'El equipo de Lunvelis ya la tiene.') + (meta.kind === 'meeting' ? detail : '') };
    if (!endpoint()) {
      savePending(record);
      toast({ type: 'progress', id: 'lp-submit', title: 'Enviando tu solicitud…', message: 'Si Shopify pide una verificación de seguridad, complétala para terminar. Al volver aquí te confirmamos que quedó registrada.', duration: 15000 });
      return;
    }
    event.preventDefault(); event.stopImmediatePropagation();
    setBusy(form, true);
    const progress = toast({ type: 'progress', id: 'lp-submit', title: 'Enviando tu solicitud…', message: 'No cierres esta página hasta ver la confirmación.' });
    viaService(form, meta, data).then((service) => {
      progress && progress.close();
      if (service) {
        remember(sig); storage().remove(draftKey(form));
        if (ui()) ui().requests.add({ ...entry, ref: service.id });
        toast({ type: 'success', sound: 'success', title: meta.successTitle, message: record.toastText + (service.emailed ? ' Te enviamos un correo de recepción.' : ''), duration: 12000 });
        renderSent({ host: form, meta, rows: record.rows, entry, emailed: !!service.emailed });
        if (window.LunvelisFx && !meta.pending) window.LunvelisFx.confetti(document.querySelector('.lp-sent') || form);
        setBusy(form, false);
      } else {
        savePending(record);
        form.dataset.lpNative = '1';
        setBusy(form, false);
        form.requestSubmit();
      }
    });
  };

  /* ---------- validación con avisos ---------- */
  let lastInvalid = 0;
  const onInvalid = (event) => {
    const el = event.target;
    if (!(el instanceof Element) || !el.closest('form[data-lp-form]')) return;
    el.setAttribute('aria-invalid', 'true');
    if (Date.now() - lastInvalid < 2500) return;
    lastInvalid = Date.now();
    const form = el.closest('form');
    const text = (node) => [...node.childNodes].filter((n) => n.nodeType === 3).map((n) => n.textContent).join(' ').replace(/\s+/g, ' ').trim();
    const legend = el.closest('fieldset') && el.closest('fieldset').querySelector('legend');
    const name = (el.type === 'radio' || el.type === 'checkbox') && legend ? legend.textContent.trim() : (el.labels && el.labels[0] ? text(el.labels[0]) : '') || el.name.replace(/^contact\[|\]$/g, '');
    toast({ type: 'warning', id: 'lp-invalid', title: 'Revisa este campo', message: `${name}: ${(el.validationMessage || 'completa este dato para continuar').replace(/\.$/, '')}.` });
    el.focus({ preventScroll: false });
    void form;
  };
  document.addEventListener('invalid', onInvalid, true);
  document.addEventListener('input', (event) => { if (event.target.getAttribute && event.target.getAttribute('aria-invalid')) event.target.removeAttribute('aria-invalid'); });

  /* ---------- reuniones: fecha, hora y zona horaria coherentes ---------- */
  const ZONES = ['America/Mexico_City', 'America/Cancun', 'America/Monterrey', 'America/Tijuana', 'America/Bogota', 'America/Lima', 'America/Santiago', 'America/Argentina/Buenos_Aires', 'America/New_York', 'America/Chicago', 'America/Los_Angeles', 'Europe/Madrid', 'UTC'];
  const todayIn = (zone) => { try { return new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date()); } catch (error) { return new Date().toISOString().slice(0, 10); } };
  const nowIn = (zone) => { try { return new Intl.DateTimeFormat('en-GB', { timeZone: zone, hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date()); } catch (error) { return '00:00'; } };
  const fixMeeting = (form) => {
    const date = form.querySelector('[name="contact[Fecha preferida]"]');
    const time = form.querySelector('[name="contact[Horario preferido]"]');
    const zone = form.querySelector('[name="contact[Zona horaria]"]');
    const range = form.querySelector('partners-date-range');
    const start = range && range.querySelector('[name="date"]');
    const end = range && range.querySelector('[name="end_date"]');
    const endHidden = form.querySelector('[data-meet-end-date]');
    if (!date || !time || !zone) return;
    if (start) start.required = false;
    if (!document.getElementById('lp-zones')) { const list = document.createElement('datalist'); list.id = 'lp-zones'; list.innerHTML = [...new Set([zone.value, ...ZONES].filter(Boolean))].map((z) => `<option value="${z}"></option>`).join(''); document.body.append(list); }
    zone.setAttribute('list', 'lp-zones');
    const validate = () => {
      let zoneOk = true;
      try { new Intl.DateTimeFormat(undefined, { timeZone: zone.value.trim() }); } catch (error) { zoneOk = false; }
      zone.setCustomValidity(zoneOk ? '' : 'Escribe una zona horaria válida, por ejemplo America/Mexico_City');
      const today = zoneOk ? todayIn(zone.value.trim()) : todayIn('UTC');
      date.min = today;
      date.setCustomValidity(date.value && date.value < today ? 'Elige hoy o una fecha futura en tu zona horaria' : '');
      time.setCustomValidity(date.value === today && time.value && zoneOk && time.value <= nowIn(zone.value.trim()) ? 'Elige un horario posterior a la hora actual' : '');
    };
    date.addEventListener('change', () => {
      if (start) { start.value = date.value; if (end && (!end.value || end.value < date.value)) end.value = date.value; if (endHidden && end) endHidden.value = end.value; start.dispatchEvent(new Event('input', { bubbles: true })); }
      validate();
    });
    for (const el of [time, zone]) el.addEventListener('input', validate);
    if (range) { range.addEventListener('change', validate); range.addEventListener('input', validate); }
    validate();
  };

  /* ---------- registro de agenda (página de reuniones) ---------- */
  const mountAgendaLog = () => {
    if (!/partners-meetings\/?$/.test(location.pathname) || document.getElementById('lp-agenda-log')) return;
    const host = document.querySelector('.lp-page__body .lp-wrap') || document.querySelector('main .lp-wrap');
    if (!host) return;
    const section = document.createElement('section');
    section.id = 'lp-agenda-log';
    section.className = 'lp-agenda-log';
    section.setAttribute('aria-labelledby', 'lp-agenda-log-title');
    section.innerHTML = '<p class="lp-kicker">Registro de agenda</p><h2 id="lp-agenda-log-title">Tus solicitudes de reunión</h2><p>Aquí queda lo que solicites desde este dispositivo. Una solicitud no es una reserva confirmada: el equipo te confirma fecha, hora y enlace por correo.</p><ul class="lp-requests lp-requests--agenda" data-lp-requests data-kind="meeting"></ul>';
    host.append(section);
    document.dispatchEvent(new CustomEvent('lp:requests'));
  };

  const mountMeter = (form) => {
    if (!/^(PartnersMeetRequest|PartnersCollaboration)/.test(form.id) || form.querySelector('.lp-meter')) return;
    const fields = () => [...form.elements].filter((el) => el.name && el.required && !el.disabled && !['hidden', 'submit', 'button'].includes(el.type));
    const meter = document.createElement('div');
    meter.className = 'lp-meter';
    meter.innerHTML = '<div class="lp-meter__top"><span data-meter-text></span></div><div class="lp-meter__bar" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-label="Avance del formulario"><span></span></div>';
    form.prepend(meter);
    const update = () => {
      const list = fields();
      const done = list.filter((el) => (el.type === 'checkbox' ? el.checked : String(el.value).trim() !== '') && el.validity.valid).length;
      const pct = list.length ? Math.round((done / list.length) * 100) : 0;
      meter.querySelector('[data-meter-text]').textContent = pct === 100 ? 'Todo listo para enviar' : `${done} de ${list.length} campos listos`;
      meter.querySelector('.lp-meter__bar').setAttribute('aria-valuenow', String(pct));
      meter.querySelector('.lp-meter__bar span').style.setProperty('--r', String(pct / 100));
      meter.classList.toggle('is-done', pct === 100);
    };
    form.addEventListener('input', update);
    form.addEventListener('change', update);
    setTimeout(update, 450);
  };

  /* ---------- arranque ---------- */
  const init = () => {
    const forms = [...document.querySelectorAll('form[action*="/contact"]')].filter((form) => form.querySelector('input[name="form_type"][value="contact"]') && form.id);
    for (const form of forms) {
      if (form.hasAttribute('data-lp-form')) continue;
      form.setAttribute('data-lp-form', '');
      rememberLabels(form);
      if (/^PartnersMeetRequest/.test(form.id)) fixMeeting(form);
      mountMeter(form);
      setTimeout(() => restoreDraft(form), 400);
      let timer;
      form.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(() => saveDraft(form), 500); });
      form.addEventListener('change', () => saveDraft(form));
    }
  };
  window.addEventListener('submit', onCapture, true);
  /* Solicitud que no llegó a completarse (por ejemplo, la verificación de Shopify quedó a medias): se muestra con honestidad. */
  const showUnsent = () => {
    const pending = storage().get(PENDING_KEY);
    if (!pending || pending.path !== location.pathname) return;
    if (Date.now() - pending.ts < 4000) return;
    const retry = () => {
      const form = document.querySelector('form[data-lp-form]#' + pending.formId) || document.querySelector('form[data-lp-form]');
      if (form) { form.scrollIntoView({ block: 'center', behavior: reduced.matches ? 'auto' : 'smooth' }); toast({ type: 'info', title: 'Revisa tus datos y pulsa Enviar', message: 'Tu información sigue guardada. Si Shopify pide verificación, complétala hasta ver la confirmación.', duration: 7000 }); }
      else location.href = pending.path;
    };
    const discard = () => { if (pending.rid && ui()) ui().requests.remove(pending.rid); storage().remove(PENDING_KEY); document.querySelectorAll('.lp-unsent').forEach((el) => el.remove()); };
    toast({ type: 'warning', id: 'lp-unsent', title: 'Tu solicitud aún no se envió', message: 'Falta completar la verificación de seguridad de Shopify. No cuenta como registrada hasta ver la confirmación.', duration: 14000, action: { label: pending.path === location.pathname ? 'Reintentar' : 'Ir al formulario', onClick: () => (pending.path === location.pathname ? retry() : (location.href = pending.path)) } });
    const log = document.getElementById('lp-agenda-log');
    if (log && pending.kind === 'meeting' && !log.querySelector('.lp-unsent')) {
      const box = document.createElement('div');
      box.className = 'lp-unsent';
      box.setAttribute('role', 'status');
      const strong = document.createElement('strong'); strong.textContent = 'Solicitud sin enviar: ' + ((pending.entry && pending.entry.title) || 'reunión');
      const detail = document.createElement('span'); detail.textContent = ((pending.entry && pending.entry.detail) ? pending.entry.detail + '. ' : '') + 'Quedó guardada como borrador en este dispositivo; falta completar la verificación de Shopify para que llegue al equipo.';
      const actions = document.createElement('div'); actions.className = 'lp-requests__actions';
      const again = document.createElement('button'); again.type = 'button'; again.textContent = 'Reintentar envío'; again.addEventListener('click', retry);
      const drop = document.createElement('button'); drop.type = 'button'; drop.textContent = 'Descartar'; drop.addEventListener('click', discard);
      actions.append(again, drop); box.append(strong, detail, actions);
      log.querySelector('[data-lp-requests]').before(box);
    }
  };
  /* Solicitudes guardadas en el servidor para quien tiene sesión: aparecen en su registro desde cualquier dispositivo. */
  const KIND_TITLE = { meeting: 'Reunión', contact: 'Mensaje de contacto', assessment: 'Diagnóstico', collaboration: 'Propuesta de colaboración' };
  const syncServer = async () => {
    const base = endpoint();
    if (!base || !/partners-meetings/.test(location.pathname)) return;
    try {
      const response = await fetch(base.replace(/public-request$/, 'my-requests'), { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const { items } = await response.json();
      const have = new Set(ui().requests.list().map((item) => item.ref).filter(Boolean));
      for (const item of (items || []).slice().reverse()) {
        if (have.has(item.id)) continue;
        const f = item.fields || {};
        const detail = [f.Modalidad, f['Fecha preferida'] && f['Horario preferido'] ? f['Fecha preferida'] + ' a las ' + f['Horario preferido'] + (f['Zona horaria'] ? ' (' + f['Zona horaria'] + ')' : '') : ''].filter(Boolean).join(' · ');
        ui().requests.add({ ts: Date.parse(item.created_at) || Date.now(), ref: item.id, kind: item.kind, title: item.kind === 'meeting' && f['Tipo de sesión'] ? 'Reunión · ' + f['Tipo de sesión'] : (KIND_TITLE[item.kind] || 'Solicitud'), detail: detail || f['Interés'] || f.Tipo || '', status: item.kind === 'meeting' ? 'Solicitud enviada · pendiente de confirmar' : 'Enviada' });
      }
    } catch (error) { /* sin sesión o sin servicio: se queda el registro local */ }
  };
  const whenUi = (fn, tries = 50) => { if (ui()) fn(); else if (tries) setTimeout(() => whenUi(fn, tries - 1), 100); };
  const start = () => { init(); mountAgendaLog(); whenUi(finalizePending); whenUi(showUnsent); whenUi(syncServer); whenUi(showChallengeSummary); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  document.addEventListener('shopify:section:load', init);
})();
